import React, { useState, useEffect } from 'react';
import AuthForm from './components/AuthForm';
import DateRangeForm from './components/DateRangeForm';
import ResultsSection from './components/ResultsSection';
import './App.css';

function App() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [data, setData] = useState({
    grouped_trade: [],
    monthly_profit: [],
    ticker_profit: [],
    adjustment_errors: [],
    available_stocks: []
  });
  const [debug, setDebug] = useState(false);
  const [debugLogs, setDebugLogs] = useState([]);

  // Debug log function
  const debugLog = (message, data) => {
    if (!debug) return;
    
    const timestamp = new Date().toLocaleTimeString();
    let logMessage = `[${timestamp}] ${message}`;
    
    if (data !== undefined) {
      if (typeof data === 'object') {
        logMessage += ': ' + JSON.stringify(data);
      } else {
        logMessage += ': ' + data;
      }
    }
    
    setDebugLogs(prevLogs => [...prevLogs, logMessage]);
    console.log(message, data);
  };

  // API configuration
  const API_CONFIG = {
    baseUrl: window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://localhost:3001/analyze'
      : '/analyze', 
    useCors: true
  };

  // Log API configuration for debugging
  useEffect(() => {
    if (debug) {
      debugLog('API Configuration', {
        hostname: window.location.hostname,
        baseUrl: API_CONFIG.baseUrl
      });
    }
  }, [debug]);

  // Fetch and analyze data
  const fetchAndAnalyze = async (sessionToken, apiToken, startDate, endDate, stockFilter) => {
    try {
      debugLog('Analyze button clicked');
      
      setLoading(true);
      setError('');
      setSuccess('');
      setShowResults(false);

      if (!sessionToken) {
        setError("Zerodha session token is required. Please enter and save it.");
        setLoading(false);
        return;
      }

      if (!apiToken) {
        setError("API token is required. Please enter and save it.");
        setLoading(false);
        return;
      }

      if (!startDate || !endDate) {
        setError("Please select both start and end dates.");
        setLoading(false);
        return;
      }
      
      // Check if API URL is configured
      if (!API_CONFIG.baseUrl) {
        setError("API URL is not configured correctly.");
        debugLog('API URL not configured properly');
        setLoading(false);
        return;
      }
      
      debugLog('Using stock filter', stockFilter ? stockFilter : 'All Stocks');

      // Use configured API URL
      let url = API_CONFIG.baseUrl;
      const cookieApiToken = getCookie('api_token');
      if (!cookieApiToken && apiToken) {
        url += `?api_token=${apiToken}`;
      }
      
      debugLog('Fetching data from', url);
      debugLog('Request payload', { 
        session_token: sessionToken ? '***' : null, 
        start_date: startDate,
        end_date: endDate,
        stock_filter: stockFilter
      });

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': apiToken // Add API token to headers
        },
        body: JSON.stringify({ 
          session_token: sessionToken,
          start_date: startDate,
          end_date: endDate,
          stock_filter: stockFilter
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        debugLog('API Error Response', errorText);
        throw new Error(`Network response was not ok: ${response.status}. Error: ${errorText}`);
      }

      const responseData = await response.json();
      
      if (responseData.error) {
        throw new Error(responseData.error);
      }
      
      debugLog('Received data', {
        grouped_trade_count: responseData.grouped_trade ? responseData.grouped_trade.length : 0,
        monthly_profit_count: responseData.monthly_profit ? responseData.monthly_profit.length : 0,
        ticker_profit_count: responseData.ticker_profit ? responseData.ticker_profit.length : 0,
        adjustment_errors_count: responseData.adjustment_errors ? responseData.adjustment_errors.length : 0,
        available_stocks_count: responseData.available_stocks ? responseData.available_stocks.length : 0
      });

      setData(responseData);
      setShowResults(true);
      setSuccess("Analysis completed successfully!");
    } catch (error) {
      debugLog('Error in fetch', error.message);
      setError("Error: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Get cookie helper function
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  return (
    <div className="container">
      <h1>Zerodha Options Trading Analysis Dashboard</h1>
      
      <div className="debug-toggle">
        <button 
          onClick={() => setDebug(!debug)} 
          style={{ 
            position: 'absolute', 
            top: '10px', 
            right: '10px',
            background: debug ? '#f44336' : '#4CAF50',
            color: 'white',
            border: 'none',
            padding: '5px 10px',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          {debug ? 'Disable Debug' : 'Enable Debug'}
        </button>
      </div>
      
      <AuthForm debugLog={debugLog} />
      
      <DateRangeForm 
        onAnalyze={fetchAndAnalyze} 
        availableStocks={data.available_stocks}
        debugLog={debugLog}
      />
      
      {loading && <div className="loading">Fetching and analyzing data, please wait...</div>}
      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}
      
      {debug && (
        <div className="debug-info" style={{ 
          marginTop: '20px', 
          padding: '10px', 
          backgroundColor: '#f8f9fa', 
          border: '1px solid #ddd',
          borderRadius: '4px',
          maxHeight: '300px',
          overflowY: 'auto',
          fontSize: '12px',
          fontFamily: 'monospace'
        }}>
          <h3>Debug Information</h3>
          <p>API URL: {API_CONFIG.baseUrl || 'Not set'}</p>
          <h4>Debug Logs:</h4>
          {debugLogs.map((log, index) => (
            <div key={index} dangerouslySetInnerHTML={{ __html: log + '<br>' }} />
          ))}
        </div>
      )}
      
      {showResults && (
        <ResultsSection data={data} />
      )}
    </div>
  );
}

export default App;
